import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datagrid from '../ui/Datagrid';
import { gridClasses } from '@mui/x-data-grid';
import columns from './table_config';
import { downloadTransactionHistory, listTransactionHistory, listTransactionPictures, updateSubmittedToInvoiceStatus } from '../../store/actions/transactionHistory';
import { Checkbox, Grid, IconButton, TablePagination, Typography } from '@mui/material';
import { renderCell } from '../ui/Datagrid/table_config';
import PropTypes from 'prop-types';
import RenderJsonField from '../ui/RenderJsonField';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import { createEpicorReportInventoryPDF } from '../../store/actions/epicor/epicorReports';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { REPORT_INVENTORY_WITH_PACK_NUM } from '../../utils/constants';
import { CameraAlt, AssignmentTurnedIn, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import PicturesModal from '../ui/PicturesModal';

// eslint-disable-next-line react/prop-types
function CustomPagination({ currentPage, limit, totalCount, handlePagination, loading }) {
  return (
    <TablePagination
      component="div"
      count={totalCount}
      page={currentPage}
      onPageChange={(event, page) => !loading && handlePagination(page)}
      rowsPerPage={limit}
      rowsPerPageOptions={[]}
    />
  );
}

const TransactionHistory = () => {
  const transactionHistory = useSelector((state) => state.transactionHistory);
  const dispatch = useDispatch();
  const [filter, setFilter] = useState([]);
  const [isPictures, setIsPictures] = useState({
    open: false,
    data: {}
  });
  const user = useSelector((state) => state.currentUser);
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (!transactionHistory.loading && transactionHistory.data.length === 0) {
      dispatch(listTransactionHistory());
    }
  }, []);

  useEffect(() => {
    if (filter.length === 0 && transactionHistory.data.length) {
      dispatch(listTransactionHistory());
    }
  }, [filter]);

  const handleDownloadPDF = (transactionOrderNum, packNum) => {
    dispatch({
      type: REDUX_ACTIONS.TRANSACTION_HISTORY_PRINT_STATE,
      payload: {
        printState: true,
      },
    });
    if (transactionOrderNum?.length) {
      transactionOrderNum?.forEach((orderNum, index) => {
        const packNumItem = packNum?.length ? packNum[index] : undefined;
        dispatch(createEpicorReportInventoryPDF(orderNum, packNumItem));
      });
    } else {
      dispatch(
        enqueueSnackbar(
          'Error in downloading file',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    }
    dispatch({
      type: REDUX_ACTIONS.TRANSACTION_HISTORY_PRINT_STATE,
      payload: {
        printState: false,
      },
    });
  };

  const handlePagination = (page) => {
    if (page < transactionHistory.currentPage) {
      dispatch(listTransactionHistory({ page: transactionHistory.currentPage - 1 }));
      dispatch({
        type: REDUX_ACTIONS.TRANSACTION_HISTORY_SET_CURRENT_PAGE,
        payload: transactionHistory.currentPage - 1
      });
    } else {
      dispatch(listTransactionHistory({ page: transactionHistory.currentPage + 1 }));
      dispatch({
        type: REDUX_ACTIONS.TRANSACTION_HISTORY_SET_CURRENT_PAGE,
        payload: page + 1
      });
    }
    apiRef.current.setFilterModel({
      items: filter
    });
    apiRef.current.scrollToIndexes({
      rowIndex: 0,
      colIndex: 0,
    });
  };

  const renderOrderField = ({ row }) => {
    let transactionOrderNum = [];
    let packNum = [];

    if (row?.type === 'Signature' && !row?.response?.ErrorMessage) {
      const orderNum =
        row?.payload?.input?.SOA ||
        `${row?.payload?.input?.OrderNum}_SOA`; // Compatibility fix for reports with OrderNum_SOA
      transactionOrderNum = [orderNum];
    } else if (
      row?.type === 'Truck to Bin Transfer' ||
      row?.type === 'Bin to Truck Transfer'
    ) {
      transactionOrderNum = [row?.response?.pakTrans];
    } else {
      transactionOrderNum = row?.response?.value?.map(
        (item) => item?.SaleOrder?.OrderNum
      );
      if (REPORT_INVENTORY_WITH_PACK_NUM.includes(row?.type)) {
        packNum = row?.response?.value?.map(
          (item) => item?.ShipParts?.PackNum
        );
      }
    }

    return (
        <Typography style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1B75D0' }} onClick={ () => {
          handleDownloadPDF(transactionOrderNum, packNum);
        }}>{transactionOrderNum?.length ? transactionOrderNum.toString() : ''}</Typography>
    );
  };

  const RenderReadyToInvoiceField = ({ row }) => {
    if (!('ReadyForInvoice' in row)) return null;

    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          sx={{ color: row?.ReadyForInvoice && 'green', pointerEvents: 'none' }}
          disableRipple
        >
          {<AssignmentTurnedIn />}
        </IconButton>
      </Grid>
    );
  };

  const RenderSubmittedToInvoiceField = ({ row }) => {
    if (row?.type !== 'Signature' &&
      row?.type !== 'Box Ticket' &&
      row?.type !== 'Portal Box Ticket' &&
      row?.type !== 'Truck Sale'
    ) {
      return null;
    }

    let transactionOrderNum = [];

    if (row?.type === 'Signature') {
      const orderNum = row?.payload?.input?.OrderNum;
      transactionOrderNum = [orderNum];
    } else {
      transactionOrderNum = row?.response?.value?.map(
        (item) => item?.SaleOrder?.OrderNum
      );
    }

    const handleChange = (e) => {
      e.stopPropagation();
      const newChecked = e.target.checked;
      if (!transactionOrderNum?.length) {
        dispatch(
          enqueueSnackbar(
            'Order Number not found',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return;
      }
      apiRef.current.updateRows([
        {
          _id: row._id,
          CompletionStatus: {
            ...row?.CompletionStatus,
            completed: newChecked
          },
        },
      ]);

      const body = {
        _id: row?._id,
        completed: newChecked,
        OrderNum: transactionOrderNum?.[0]
      };
      dispatch(updateSubmittedToInvoiceStatus(body));
    };

    return (
    <Checkbox
      checked={!!row?.CompletionStatus?.completed}
      onChange={handleChange}
      icon={
        <CheckBoxOutlineBlank
          sx={{ color: 'gray' }}
        />
      }
      checkedIcon={
        <CheckBox
          sx={{ color: 'green' }}
        />
      }
    />
    );
  };

  const renderPicturesCell = (props) => {
    const rowData = props?.row;
    const picturesArray = rowData?.pictures || rowData?.payload?.pictures || rowData?.payload?.input?.pictures;
    return (
        <Grid container justifyContent="center">
          <IconButton
            variant="solid"
            disabled={transactionHistory.loading}
            sx={{ display: !picturesArray?.length && 'none' }}
            onClick={() => {
              setIsPictures({
                open: true,
                data: rowData
              });
            }}
          >
            <CameraAlt />
          </IconButton>
        </Grid>
    );
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        <Datagrid
          apiRef={apiRef}
          data={transactionHistory?.data}
          columns={columns(renderCell, RenderJsonField, renderOrderField, renderPicturesCell, RenderSubmittedToInvoiceField, RenderReadyToInvoiceField, user)}
          loading={transactionHistory.loading}
          disableStickyHeader={false}
          onRowClick={({ row }) => {
            const { transactionOrderNum, transactionPakNum } =
              (row?.response?.value ?? []).reduce(
                (acc, item) => {
                  acc.transactionOrderNum.push(item?.SaleOrder?.OrderNum);
                  if (REPORT_INVENTORY_WITH_PACK_NUM.includes(row?.type)) {
                    acc.transactionPakNum.push(item?.ShipParts?.PackNum);
                  }
                  return acc;
                },
                {
                  transactionOrderNum: [],
                  transactionPakNum: [],
                }
              );
            if (transactionOrderNum?.length) {
              dispatch({
                type: REDUX_ACTIONS.TRANSACTION_HISTORY_ORDER_NUM,
                payload: {
                  orderNum: transactionOrderNum,
                  pakNum: transactionPakNum?.length && transactionPakNum
                },
              });
            } else {
              dispatch({
                type: REDUX_ACTIONS.TRANSACTION_HISTORY_ORDER_NUM,
                payload: {
                  orderNum: null,
                },
              });
            }
          }}
          height={'90vh'}
          getRowId={(row) => row._id}
          autoPageSize={false}
          autoHeight={false}
          pagination={false}
          hideFooter
          experimentalFeatures={{ newEditingApi: true }}
          filterMode="server"
          getRowHeight={() => 'auto'}
          serverExport={() => dispatch(downloadTransactionHistory())}
          sx={{
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold !important',
            },
            '.MuiDataGrid-cell': {
              fontSize: 'medium',
              fontWeight: 'bold !important'
            },
            [`& .${gridClasses.cell}`]: {
              py: 1,
            },
          }}
          toolbar={{
            options: {
              columns: true,
              filters: true,
              export: {
                enabled: true,
                options: true,
                serverExportMode: true
              },
            },
          }}
          onFilterModelChange={ (model) => {
            setFilter(model.items);
            const items = model.items.map(filter => {
              if (filter.field === 'userEmail' || filter.field === 'response' || filter.field === 'payload') {
                return { ...filter, field: 'details.' + filter.field };
              } else if (filter.field === 'orderNumber') {
                return { ...filter, field: 'details.response.value.SaleOrder.OrderNum' };
              } else {
                return filter;
              }
            });
            const filterModels = { ...model, items };
            dispatch({
              type: REDUX_ACTIONS.TRANSACTION_HISTORY_SET_FILTER,
              payload: filterModels.items.length ? filterModels : {}
            });
          }}
          sortingMode="server"
          onSortModelChange={(sortModel) => {
            const sort = sortModel.reduce((result, item) => {
              if (item.field === 'userEmail' || item.field === 'payload' || item.field === 'response') {
                result['details.' + item.field] = item.sort;
              } else if (item.field === 'orderNumber') {
                result['details.response.value.SaleOrder.OrderNum'] = item.sort;
              } else {
                result[item.field] = item.sort;
              }
              return result;
            }, {});
            dispatch({
              type: REDUX_ACTIONS.TRANSACTION_HISTORY_SET_SORT,
              payload: sort
            });
            dispatch({
              type: REDUX_ACTIONS.TRANSACTION_HISTORY_SET_CURRENT_PAGE,
              payload: 1
            });
            dispatch(listTransactionHistory());
          }}
        />
        <CustomPagination
          currentPage={transactionHistory.currentPage - 1}
          limit={transactionHistory.limit}
          totalCount={transactionHistory.totalCount}
          handlePagination={handlePagination}
          loading={transactionHistory.loading}
        />
      </Grid>
      {isPictures.open
        ? <PicturesModal
          open={isPictures.open}
          data={isPictures.data}
          handleClose={() => {
            setIsPictures({
              open: false,
              data: {}
            });
          }}
          getPictures={listTransactionPictures}
        />
        : null}
    </Grid>
  );
};

TransactionHistory.propTypes = {
  row: PropTypes.object,
};

export default TransactionHistory;
